import React, { useState } from "react";
import { Button, Loader } from "semantic-ui-react";
import { pullCreditScore } from "../../../api/BrokerApi";
import { pullMockCreditScore } from "../../../api/mock/MockBrokerApi";
import { converToSSNString, convertToDateString, getDropdownOptions, isValidPermission, setBackgroundColor } from "../../../Helpers";
import { getIsWhiteLabeled } from "../../../helpers/PermissionHelper";
import { BrokerConstants } from "../../constants/BrokerConstants";
import { GlobalConstants } from "../../constants/GlobalConstants";
import { FeatureFlags } from "../../constants/LocalServerConstants";
import BCModal from "../../ui/BCModal";
import BPLoanALReoTables from "./BPLoanALReoTables";
import BPLoanDetailSection from "./BPLoanDetailSection";

interface ILoanGuarantorInfoContentProps {
  accordionProps: any;
  applicationMetadata: any;
  borrowerApplicationId: any;
  brokerColor: any;
  permissions: any;
  proxyUserId: any;
  isEditMode: boolean;
  isLoanInfo: any;
  isGuarantor: any;
  invalidProps: any;
  guarantorAccordions: any;
  guarantor: any;
  guarantorPatchObj: any;
  setGuarantorPatchObj: any;
  handleALDeleteAsync: any;
  handleREODeleteAsync: any
}

const BPLoanGuarantorInfoContent = ({ ...props }: ILoanGuarantorInfoContentProps) => {
  const [contactInfoPatchObj, setContactInfoPatchObj] = useState({ id: props.guarantor.id });
  const [personalDetailsPatchObj, setPersonalDetailsPatchObj] = useState({});
  const [revealSSN, setRevealSSN] = useState(false as boolean);
  const [revealDOB, setRevealDOB] = useState(false as boolean);
  const [isCreditModalOpen, setIsCreditModalOpen] = useState(false as boolean);
  const [creditModalStage, setCreditModalStage] = useState("" as string);
  const [creditScoreGuarantor, setCreditScoreGuarantor] = useState({} as any);

  const contactInfomationTitle = BrokerConstants.InteriorCardGuarantorAccordions.ContactInformation;
  const personalDetailsTitle = BrokerConstants.InteriorCardGuarantorAccordions.PersonalDetails;
  const creditScoreTitle = BrokerConstants.InteriorCardGuarantorAccordions.CreditScore;
  const disclosureTitle = BrokerConstants.InteriorCardGuarantorAccordions.DisclosureStatuses;
  const assetAccordionId = props.guarantorAccordions.find(x => x.guarantorId === props.guarantor.id && x.name === BrokerConstants.InteriorCardGuarantorAccordions.Assets)?.id;
  const liabilitiesAccordionId = props.guarantorAccordions.find(x => x.guarantorId === props.guarantor.id && x.name === BrokerConstants.InteriorCardGuarantorAccordions.Liabilities)?.id;
  const reoAccordionId = props.guarantorAccordions.find(x => x.guarantorId === props.guarantor.id && x.name === BrokerConstants.InteriorCardGuarantorAccordions.ReoSchedules)?.id;
  
  const isAuthorized = () => {
    if (props.isGuarantor(props.guarantor) || isValidPermission(props.permissions, "GlobalAdminAccess", "read")) {
      return true;
    }

    return false;
  };

  const handleIsCreditPullValid = (guarantor) => {
    if (guarantor.isCreditScorePulled === false && isAuthorized()) {
      return true;
    }

    return false;
  };

  const handlePullCreditScore = async () => {
    setIsCreditModalOpen(true);
    setCreditModalStage("pulling");
    if (props.guarantor && props.guarantor.id) {
      const user = props.guarantor;
      const creditUser = {
        "BorrowerApplicationId": props.borrowerApplicationId,
        "OrganizationContactId": user.id,
        "SocialSecurityNumber": user.socialSecurityNumber,
        "FirstName": user.firstName,
        "LastName": user.lastName,
        "StreetAddressLine": user.physicalAddress,
        "CityName": user.physicalCity,
        "StateName": user.physicalState,
        "ZipCode": user.physicalZip
      };

      const isPullCreditScoreSuccess = await handlePullCreditScoreAsync(creditUser);
      let creditModalStage = "pull failed";
      if (isPullCreditScoreSuccess) {
        creditModalStage = "pulled";
      }

      setCreditModalStage(creditModalStage);
    }
  };

  const handleCreditModal = (creditModalStage) => {
    const creditModal = [] as any;
    if (creditModalStage === "pulling") {
      creditModal.content = <div className="credit-score-modal">
        <div className="credit-score-modal-title">Your credit is being pulled, please wait.</div>
        <Loader className="modal-loader" active inline="centered" size={"big"} />
      </div>;
    }
    else if (creditModalStage === "pulled") {
      creditModal.content = <div className="credit-score-modal">
        <div className="credit-score-modal-title">Success! We were able to retrieve your credit score.</div>
        <div className="credit-score-modal-credit-container">
          <span className="credit-score-modal-subtext">Your credit score is</span>
          <span className="credit-score-modal-credit">{creditScoreGuarantor.creditScore ? creditScoreGuarantor.creditScore : ""}</span>
        </div>
      </div>;
      creditModal.actions = <Button
        className="bp-button"
        style={getIsWhiteLabeled() ? setBackgroundColor(props.brokerColor ? props.brokerColor : "#000000") : void 0}
        fluid
        onClick={() => handleCreditModalClose()}
      >
        <div className="gmr-button-container">
          <div className="gmr-button-text">Close</div>
        </div>
      </Button>;
    }
    else if (creditModalStage === "pull failed") {
      creditModal.content = <div className="credit-score-modal">
        <div className="credit-score-modal-title">We were unable to retrieve your credit score.</div>
        <div className="credit-score-modal-credit-failed-container">
          <div className="credit-score-modal-subtext">Common causes include incorrect entry of:</div>
          <div className="credit-score-modal-subtext-padding">1. Social Security Number (SSN)</div>
          <div className="credit-score-modal-subtext">2. Legal first and last name</div>
          <div className="credit-score-modal-subtext">3. Accurate physical address</div>
          <div className="credit-score-modal-subtext-padding">Please verify the information you entered and try again.</div>
        </div>
      </div>;
      creditModal.actions = <Button
        className="bp-button"
        style={getIsWhiteLabeled() ? setBackgroundColor(props.brokerColor ? props.brokerColor : "#000000") : void 0}
        fluid
        onClick={() => handleCreditModalClose()}
      >
        <div className="gmr-button-container">
          <div className="gmr-button-text">Close</div>
        </div>
      </Button>;
    }

    return creditModal;
  };

  const handleCreditModalClose = () => {
    setIsCreditModalOpen(false);
  };

  const handlePullCreditScoreAsync = async (creditUser) => {
    try {
      const response = FeatureFlags.IS_MOCK ? await pullMockCreditScore(creditUser) : await pullCreditScore(creditUser, props.proxyUserId) as any;
      if (response && response.ok) {
        if (props.guarantor) {
          props.guarantor.creditScore = await response.json();
          setCreditScoreGuarantor(props.guarantor);
          return true;
        }
      }
      else {
        setCreditModalStage("pull failed");
      }
    }
    catch (error) {
      setCreditModalStage("pull failed");
    }

    return false;
  };

  const handleGuarantorAddressChange = (subObj, mainObj, setMainObj) => {
    mainObj.contactInformation = {...mainObj.contactInformation, ...subObj};
    setMainObj({...mainObj});
  };

  return (
    <React.Fragment>
      <BCModal
        size="tiny"
        isModalOpen={isCreditModalOpen}
        onClose={() => creditModalStage !== "pulling" ? handleCreditModalClose() : void 0}
        onOpen={() => setIsCreditModalOpen(true)}
        content={handleCreditModal(creditModalStage).content && handleCreditModal(creditModalStage).content}
        actions={handleCreditModal(creditModalStage).actions && handleCreditModal(creditModalStage).actions}
        brokerColor={getIsWhiteLabeled() ? props.brokerColor : void 0}
      />
      <BPLoanDetailSection
        title={contactInfomationTitle}
        accordionId={props.guarantorAccordions.find(x => x.guarantorId === props.guarantor.id && x.name === contactInfomationTitle)?.id}
        mainPatchObj={props.guarantorPatchObj}
        setMainPatchObj={props.setGuarantorPatchObj}
        subPatchObjName="contactInformation"
        subPatchObj={contactInfoPatchObj}
        setSubPatchObj={setContactInfoPatchObj}
        isEditMode={props.isEditMode}
        {...props.accordionProps}
        invalidProps={props.invalidProps}
        validationGroupName={`guarantor-${props.guarantor.id}`}
        inputs={[
          {
            value: props.guarantor.firstName,
            label: "First Name:",
            field: "firstName",
            size: 4,
            required: true
          },
          {
            value: props.guarantor.lastName,
            label: "Last Name:",
            field: "lastName",
            size: 4,
            required: true
          },
          {
            value: props.guarantor.email,
            label: "Email:",
            field: "email",
            size: 4,
            readonly: true
          },
          {
            value: props.guarantor.mobilePhone,
            label: "Mobile Phone:",
            field: "mobilePhone",
            size: 4
          },
          {
            value: props.guarantor.officePhone,
            label: "Office Phone:",
            field: "officePhone",
            size: 4
          },
          {
            value: props.guarantor.homePhone,
            label: "Home Phone:",
            field: "homePhone",
            size: 4
          },
          {
            value: props.guarantor,
            original: props.guarantor,
            address: true,
            addressObj: {
              address: props.guarantor.physicalStreet,
              city: props.guarantor.physicalCityName,
              state: props.guarantor.physicalStateName,
              zip: props.guarantor.physicalZip,
            },
            defaultValueObj: {...props.guarantor},
            placeDetailsObject: contactInfoPatchObj,
            setPlaceDetailsObject: (placeDetailsObject) => handleGuarantorAddressChange(placeDetailsObject, props.guarantorPatchObj, props.setGuarantorPatchObj),
            required: true,
            fieldsNameOverridesArray: [
              {
                addressComponent: "address",
                field: "physicalStreet"
              },
              {
                addressComponent: "city",
                field: "physicalCityName"
              },
              {
                addressComponent: "state",
                field: "physicalStateName"
              },
              {
                addressComponent: "county",
                field: "phsyicalCountyName"
              },
              {
                addressComponent: "zip",
                field: "physicalZip"
              },
            ],
            label: "Address",
            size: 8
          }
        ]}
      />
      {props.isGuarantor(props.guarantor) && <React.Fragment>
        <BPLoanDetailSection
          title={personalDetailsTitle}
          accordionId={props.guarantorAccordions.find(x => x.guarantorId === props.guarantor.id && x.name === personalDetailsTitle)?.id}
          mainPatchObj={props.guarantorPatchObj}
          setMainPatchObj={props.setGuarantorPatchObj}
          subPatchObjName="additionalInformation"
          subPatchObj={personalDetailsPatchObj}
          setSubPatchObj={setPersonalDetailsPatchObj}
          isEditMode={props.isEditMode}
          {...props.accordionProps}
          inputs={[
            {
              valueHidden: !revealSSN,
              value: !isAuthorized() ? converToSSNString(props.guarantor.socialSecurityNumber) : revealSSN ? props.guarantor.socialSecurityNumber : converToSSNString(props.guarantor.socialSecurityNumber),
              label: !isAuthorized() ? "SSN:" : undefined,
              actionText: isAuthorized() ? (revealSSN ? "Hide" : "Reveal") : undefined,
              actionLabel: "SSN:",
              actionLabelClass: "sub-action-label",
              labelAction: () => setRevealSSN(!revealSSN),
              size: 8,
              field: "socialSecurityNumber",
              inputType: revealSSN ? "socialSecurity" : "",
              readonly: !isAuthorized()
            },
            {
              calendar: true,
              valueHidden: !revealDOB,
              value: !isAuthorized() ? "" : revealDOB ? props.guarantor.dateOfBirth : "",
              label: !isAuthorized() ? "Date of Birth:" : undefined,
              actionText: isAuthorized() ? (revealDOB ? "Hide" : "Reveal") : undefined,
              actionLabel: "Date of Birth:",
              actionLabelClass: "sub-action-label",
              labelAction: () => setRevealDOB(!revealDOB),
              size: 8,
              field: "dateOfBirth",
              readonly: !isAuthorized()
            },
            {
              value: props.guarantor.maritalStatusId,
              label: "Marital Status:",
              size: 4,
              field: "maritalStatusId",
              dropdown: getDropdownOptions(props.applicationMetadata.maritalStatuses, ["id"], "name"),
            },
            {
              value: props.guarantor.spouseName,
              label: "Spouse's Name:",
              size: 4,
              field: "spouseName"
            },
            {
              value: props.guarantor.employerName,
              label: "Employer Name:",
              size: 4,
              field: "employerName"
            },
            {
              value: props.guarantor.employerPosition,
              label: "Title:",
              size: 4,
              field: "employerPosition"
            },
            {
              value: props.guarantor.yearsInJob,
              label: "Years in Current Job:",
              size: 4,
              field: "yearsInJob",
              unitType: (props.guarantor.yearsInJob && props.guarantor.yearsInJob > 1) ? "Years" : "Year"
            },
            {
              value: props.guarantor.yearsInBusiness,
              label: "Years in Business:",
              size: 4,
              field: "yearsInBusiness",
              unitType: (props.guarantor.yearsInJob && props.guarantor.yearsInJob > 1) ? "Years" : "Year"
            },
            {
              value: props.guarantor.monthlyRevenue,
              label: "Gross Revenue:",
              size: 4,
              field: "monthlyRevenue",
              unitType: "$"
            },
            {
              value: props.guarantor.monthlyIncome,
              label: "Net Income:",
              size: 4,
              field: "monthlyIncome",
              unitType: "$"
            },
            {
              value: props.guarantor.yearsInIndustry,
              label: "Years in Industry:",
              field: "yearsInIndustry",
              size: 4,
              unitType: (props.guarantor.yearsInJob && props.guarantor.yearsInJob > 1) ? "Years" : "Year"
            },
            {
              checkbox: true,
              value: props.guarantor.uscitizen,
              label: "Are You a US Citizen?",
              field: "uscitizen",
              size: 4
            },
            {
              value: props.guarantor.citizenshipStatus,
              label: "Status:",
              field: "citizenshipStatus",
              size: 8
            },
            {
              checkbox: true,
              value: props.guarantor.pendingLegalAction,
              label: "Pending Legal Action?",
              field: "pendingLegalAction",
              size: 4
            },
            {
              value: props.guarantor.legalActionDescription,
              label: "Description of Legal Action:",
              field: "legalActionDescription",
              size: 12
            },
            {
              checkbox: true,
              value: props.guarantor.isForeclosureInitiated,
              label: "Foreclosure Initiated?",
              field: "isForeclosureInitiated",
              size: 4
            },
            {
              value: props.guarantor.foreclosureDescription,
              label: "Description of Foreclosure:",
              field: "foreclosureDescription",
              size: 12
            },
            {
              value: props.guarantor.otherCreditName,
              label: "Other Names under which you have been granted credit:",
              field: "otherCreditName",
              size: 16
            }
          ]}
        />
        <BPLoanALReoTables
          accordionProps={props.accordionProps}
          applicationMetadata={props.applicationMetadata}
          brokerColor={props.brokerColor}
          invalidProps={props.invalidProps}
          isEditMode={props.isEditMode}
          assetTitle={BrokerConstants.InteriorCardGuarantorAccordions.Assets}
          assetAccordionId={assetAccordionId}
          liabilitiesTitle={BrokerConstants.InteriorCardGuarantorAccordions.Liabilities}
          liabilitiesAccordionId={liabilitiesAccordionId}
          reoTitle={BrokerConstants.InteriorCardGuarantorAccordions.ReoSchedules}
          reoAccordionId={reoAccordionId}
          isOrganization={false}
          organizationContact={props.guarantor}
          mainPatchObj={props.guarantorPatchObj}
          setMainPatchObj={props.setGuarantorPatchObj}
          handleALDeleteAsync={props.handleALDeleteAsync}
          handleREODeleteAsync={props.handleREODeleteAsync}
        />
        <BPLoanDetailSection
          title={creditScoreTitle}
          accordionId={props.guarantorAccordions.find(x => x.guarantorId === props.guarantor.id && x.name === creditScoreTitle)?.id}
          mainPatchObj={props.guarantorPatchObj}
          setMainPatchObj={props.setGuarantorPatchObj}
          subPatchObjName="additionalInformation"
          subPatchObj={personalDetailsPatchObj}
          setSubPatchObj={setPersonalDetailsPatchObj}
          isEditMode={props.isEditMode && !props.guarantor.creditScore}
          {...props.accordionProps}
          invalidProps={props.invalidProps}
          validationGroupName={`guarantor-${props.guarantor.id}-creditScore`}
          inputs={[
            {
              actionText: handleIsCreditPullValid(props.guarantor) ? "Pull Credit Score" : undefined,
              actionLabel: props.guarantor.creditScoreUpdatedOn !== null && props.guarantor.creditScore ? "Credit Score:" : "Estimated Credit Score",
              actionLabelClass: "sub-action-label",
              labelAction: () => handlePullCreditScore(),
              value: props.guarantor.creditScoreUpdatedOn !== null && props.guarantor.creditScore ? props.guarantor.creditScore : props.guarantor.estimatedCreditScore,
              inputType: GlobalConstants.InputTypes.INT,
              label: handleIsCreditPullValid(props.guarantor) ? undefined : props.guarantor.creditScoreUpdatedOn !== null && props.guarantor.creditScore ? "Credit Score:" : "Estimated Credit Score",
              size: 4,
              field: props.guarantor.creditScoreUpdatedOn !== null && props.guarantor.creditScore ? "creditScore" : "estimatedCreditScore",
              readonly: props.guarantor.creditScore,
            }
          ]}
        />
        <BPLoanDetailSection
          title={disclosureTitle}
          accordionId={props.guarantorAccordions.find(x => x.guarantorId === props.guarantor.id && x.name === disclosureTitle)?.id}
          mainPatchObj={props.guarantorPatchObj}
          setMainPatchObj={props.setGuarantorPatchObj}
          subPatchObj={props.guarantorPatchObj}
          setSubPatchObj={props.setGuarantorPatchObj}
          isEditMode={false}
          {...props.accordionProps}
          inputs={[
            {
              value: props.guarantor.disclosureCompletedOn ? props.guarantor.disclosureCompletedOn : "No",
              label: "Disclosure Completed Date:",
              size: 4,
              readonly: true
            },
            {
              value: props.guarantor.submittedOn ? convertToDateString(props.guarantor.submittedOn, "full") : "No",
              label: "Submitted Date:",
              size: 6,
              readonly: true
            },
            {
              value: props.guarantor.creditScoreUpdatedOn ? convertToDateString(props.guarantor.creditPulledOn, "full") : "Not Pulled",
              label: "Credit Report Pulled Date:",
              size: 8,
              readonly: true
            },
            {
              value: (props.guarantor.creditScore && props.guarantor.isCreditScorePulled === false) ? "Expired" : props.guarantor.creditScore ? "Not Expired" : "Not Pulled",
              label: "Credit Report Pulled Status:",
              size: 6,
              readonly: true
            },
          ]}
        />
      </React.Fragment>}
      {FeatureFlags.IS_DEBUG && <React.Fragment>
        <button onClick={() => console.log(props.guarantor)}>props.guarantor</button>
        <button onClick={() => console.log(contactInfoPatchObj)}>contactInfoPatchObj</button>
        <button onClick={() => console.log(props.guarantorPatchObj)}>props.guarantorPatchObj</button>
      </React.Fragment>}
    </React.Fragment>
    );
  };
  
  export default BPLoanGuarantorInfoContent;